import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationItem } from '@packages/types';
import { ApiError, queryKeys, getNextPageParam } from '@packages/api';
import {
  archiveNotification,
  archiveReadNotifications,
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@packages/api/endpoints/notification.js';
import { useOptimisticListMutation } from '../../hooks/useOptimisticListMutation';
import {
  applyAllNotificationsReadToCache,
  applyNotificationReadToCache,
  removeNotificationFromCache,
} from './notification-cache';

const UNREAD_COUNT_POLL_MS = 60_000;
const LIST_PAGE_SIZE = 25;

/** Prefix of every cached list variant, so a patch reaches them all but not the count. */
const notificationListsKey = (accountId: string) => [...queryKeys.notifications(accountId), 'list'] as const;

/** The bell badge. Polls only while the tab is visible. */
export function useUnreadNotificationCount(accountId: string) {
  return useQuery({
    queryKey: queryKeys.notificationUnreadCount(accountId),
    queryFn: () => getUnreadNotificationCount(accountId),
    enabled: !!accountId,
    refetchInterval: UNREAD_COUNT_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

/** The popover list. Only fetched while the popover is open. */
export function useNotificationList(accountId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: queryKeys.notificationList(accountId),
    queryFn: ({ pageParam }) => listNotifications(accountId, { cursor: pageParam, limit: LIST_PAGE_SIZE }),
    getNextPageParam,
    initialPageParam: undefined as string | undefined,
    enabled: enabled && !!accountId,
  });
}

export function useMarkNotificationRead(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // A 404 means another session already archived the row: nothing left to
    // mark, so it is dropped from the list rather than toasted as an error.
    mutationFn: async (item: NotificationItem) => {
      try {
        await markNotificationRead(accountId, item.id);
        return 'marked' as const;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return 'gone' as const;
        throw error;
      }
    },
    onSuccess: (outcome, item) => {
      queryClient.setQueriesData({ queryKey: notificationListsKey(accountId) }, (old: unknown) =>
        outcome === 'gone'
          ? removeNotificationFromCache(old, item.id)
          : applyNotificationReadToCache(old, item, new Date().toISOString()),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationUnreadCount(accountId) });
    },
  });
}

export function useMarkAllNotificationsRead(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(accountId),
    onSuccess: () => {
      queryClient.setQueriesData({ queryKey: notificationListsKey(accountId) }, (old: unknown) =>
        applyAllNotificationsReadToCache(old, new Date().toISOString()),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationUnreadCount(accountId) });
    },
  });
}

/** Clearing removes every read notification from the list, so the rows leave. */
export function useClearReadNotifications(accountId: string) {
  return useOptimisticListMutation<unknown, Error, void, NotificationItem>({
    mutationFn: () => archiveReadNotifications(accountId),
    listQueryKey: notificationListsKey(accountId),
    match: (item) => item.readAt !== null,
  });
}

/** Clearing one notification removes its row; the unread badge follows when it was unread. */
export function useArchiveNotification(accountId: string) {
  return useOptimisticListMutation<'archived' | 'gone', Error, NotificationItem, NotificationItem>({
    // A 404 means the row is already gone (another session archived it), which is
    // the outcome the user asked for, so it is not an error.
    mutationFn: async (item) => {
      try {
        await archiveNotification(accountId, item.id);
        return 'archived';
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return 'gone';
        throw error;
      }
    },
    listQueryKey: notificationListsKey(accountId),
    match: (row, item) => row.id === item.id,
    alsoInvalidate: [queryKeys.notificationUnreadCount(accountId)],
  });
}
